import projectValue from '#calculator/projectValue'
import type { TaxCalculationResult } from '#calculator/types'
import { TaxKey, type TaxMetadata } from '#dictionary'
import { FilingStatus } from '#dictionary/filingStatuses'
import type { Region } from '#dictionary/regions'
import { TAX_BY_KEY } from '#dictionary/taxes'
import {
  STATE_BRACKETS,
  type TaxStateBracket,
} from '#dictionary/taxStateBrackets'

import type {
  ITaxCalculator,
  TaxBracketBreakdown,
  TaxVarietyWithFilingStatusAndRegion,
} from './types'

class StateTax implements ITaxCalculator<TaxVarietyWithFilingStatusAndRegion> {
  public readonly metadata: TaxMetadata = TAX_BY_KEY.get(TaxKey.TaxStateIncome)!
  private readonly bracketsByYearFilingStatusAndRegion: Map<
    string,
    TaxStateBracket[]
  >

  constructor(private readonly maxPredictionsYear: number) {
    this.bracketsByYearFilingStatusAndRegion = new Map()
    this.initializeBrackets()
    this.predictBrackets()
  }

  private initializeBrackets(): void {
    STATE_BRACKETS.forEach(bracket => {
      const key = `${bracket.year}-${bracket.filingStatus}-${bracket.region}`
      if (!this.bracketsByYearFilingStatusAndRegion.has(key)) {
        this.bracketsByYearFilingStatusAndRegion.set(key, [])
      }
      this.bracketsByYearFilingStatusAndRegion.get(key)!.push(bracket)
    })
  }

  private predictBrackets(): void {
    // Group brackets by filing status and region for prediction
    const bracketsByFilingStatusAndRegion = new Map<string, TaxStateBracket[]>()

    STATE_BRACKETS.forEach(bracket => {
      const key = `${bracket.filingStatus}-${bracket.region}`
      if (!bracketsByFilingStatusAndRegion.has(key)) {
        bracketsByFilingStatusAndRegion.set(key, [])
      }
      bracketsByFilingStatusAndRegion.get(key)!.push(bracket)
    })

    bracketsByFilingStatusAndRegion.forEach(
      (brackets, filingStatusAndRegion) => {
        // Find the latest year for this filing status and region combination
        let lastYear = 0
        let lastYearBrackets: TaxStateBracket[] = []

        brackets.forEach(bracket => {
          if (bracket.year > lastYear) {
            lastYear = bracket.year
            lastYearBrackets = brackets.filter(b => b.year === bracket.year)
          }
        })

        if (lastYear > this.maxPredictionsYear) {
          return
        }

        // Predict brackets for future years
        for (let year = lastYear + 1; year <= this.maxPredictionsYear; year++) {
          const predictedBrackets = lastYearBrackets.map(bracket => ({
            ...bracket,
            year,
            to: projectValue(
              bracket.to,
              this.metadata.growthRate,
              bracket.year,
              year
            ),
          }))

          const key = `${year}-${filingStatusAndRegion}`
          this.bracketsByYearFilingStatusAndRegion.set(key, predictedBrackets)
        }
      }
    )
  }

  public getTaxRate(
    year: number,
    variety: TaxVarietyWithFilingStatusAndRegion
  ): number {
    const brackets = this.getBracketsForYearFilingStatusAndRegion(
      year,
      variety.filingStatus,
      variety.region
    )

    if (brackets.length === 0) {
      return 0
    }

    // Return the highest tax rate as a rough estimate
    return Math.max(...brackets.map(bracket => bracket.taxRate))
  }

  public getTaxableThreshold(
    year: number,
    variety: TaxVarietyWithFilingStatusAndRegion
  ): number {
    const brackets = this.getBracketsForYearFilingStatusAndRegion(
      year,
      variety.filingStatus,
      variety.region
    )
    return Math.max(...brackets.map(bracket => bracket.to))
  }

  public calculate(
    amount: number,
    year: number,
    variety: TaxVarietyWithFilingStatusAndRegion
  ): TaxCalculationResult {
    const brackets = this.getBracketsForYearFilingStatusAndRegion(
      year,
      variety.filingStatus,
      variety.region
    )

    if (brackets.length === 0) {
      return this.createEmptyResult(amount)
    }

    return this.calculateTaxWithBrackets(
      amount,
      brackets,
      year,
      variety.filingStatus,
      variety.region
    )
  }

  private getBracketsForYearFilingStatusAndRegion(
    year: number,
    filingStatus: FilingStatus,
    region: Region
  ): TaxStateBracket[] {
    const key = `${year}-${filingStatus}-${region}`
    return this.bracketsByYearFilingStatusAndRegion.get(key) || []
  }

  private calculateTaxWithBrackets(
    amount: number,
    brackets: TaxStateBracket[],
    year: number,
    filingStatus: FilingStatus,
    region: Region
  ): TaxCalculationResult {
    let totalTax = 0
    let remainingIncome = amount
    const bracketBreakdown: TaxBracketBreakdown[] = []

    for (let i = 0; i < brackets.length; i++) {
      const bracket = brackets[i]
      const bracketFrom = this.calculateBracketFromValue(brackets, i)
      const bracketUpperLimit = bracket.to

      // Calculate how much income falls into this bracket
      const incomeInBracket = Math.max(
        0,
        Math.min(remainingIncome, bracketUpperLimit - bracketFrom)
      )

      if (incomeInBracket > 0) {
        // Calculate tax for this bracket
        const taxInBracket = incomeInBracket * bracket.taxRate
        totalTax += taxInBracket

        bracketBreakdown.push({
          year,
          filingStatus,
          from: bracketFrom,
          to: bracketUpperLimit,
          taxRate: bracket.taxRate,
          description: `State tax bracket ${i + 1} (${region})`,
          incomeInBracket,
          taxInBracket,
        })

        remainingIncome -= incomeInBracket
      }

      // If no more income to tax, break
      if (remainingIncome <= 0) {
        break
      }
    }

    // Calculate effective tax rate
    const effectiveRate = amount > 0 ? totalTax / amount : 0

    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome: amount,
      standardDeduction: 0, // State tax calculation doesn't include standard deduction
      effectiveRate,
      totalTax,
      bracketBreakdown,
    }
  }

  private calculateBracketFromValue(
    brackets: TaxStateBracket[],
    index: number
  ): number {
    return index === 0 ? 0 : brackets[index - 1].to
  }

  private createEmptyResult(amount: number): TaxCalculationResult {
    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome: amount,
      standardDeduction: 0,
      effectiveRate: 0,
      totalTax: 0,
      bracketBreakdown: [],
    }
  }
}

export default StateTax
